import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WCST — vue React staircase + debrief (cyanotype Wave M2-game-chrome).
 *
 * Layout : top row = 4 reference piles (cliquables). Center = test card.
 * Feedback = check / cross neutre, ne revele pas la regle courante (Berg 1948).
 *
 * DEC-018 : aucun claim chiffre dans la copy. ScientificCaveat cite Berg 1948
 * (doi 10.1080/00221309.1948.9918159), Heaton WCST manual, Miyake et al. 2000
 * (doi 10.1006/cogp.1999.0734).
 */
import { useCallback, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig } from './params';
import { REFERENCE_PILES } from './types';
const COLOR_HEX = {
    red: '#c64141',
    green: '#3f9c5a',
    yellow: '#d6a52b',
    blue: '#2b6cbf',
};
function Shape({ shape, color, size }) {
    const fill = COLOR_HEX[color];
    if (shape === 'circle') {
        return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": true, children: _jsx("circle", { cx: "12", cy: "12", r: "9", fill: fill }) }));
    }
    if (shape === 'triangle') {
        return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": true, children: _jsx("polygon", { points: "12,3 21,21 3,21", fill: fill }) }));
    }
    if (shape === 'cross') {
        return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": true, children: [_jsx("rect", { x: "10", y: "3", width: "4", height: "18", fill: fill }), _jsx("rect", { x: "3", y: "10", width: "18", height: "4", fill: fill })] }));
    }
    // star
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": true, children: _jsx("polygon", { points: "12,2 14.7,8.6 21.8,9.3 16.4,14 18,21 12,17.3 6,21 7.6,14 2.2,9.3 9.3,8.6", fill: fill }) }));
}
function CardFace({ card, size }) {
    const cols = Math.min(card.number, 2);
    const rows = Math.ceil(card.number / cols);
    const shapes = Array.from({ length: card.number });
    return (_jsx("div", { className: "flex items-center justify-center bg-cyan-surface border border-cyan-border", style: {
            width: size,
            height: size * 1.4,
            padding: 6,
        }, children: _jsx("div", { className: "grid items-center justify-center", style: {
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                gap: 4,
            }, children: shapes.map((_, i) => (_jsx(Shape, { shape: card.shape, color: card.color, size: Math.floor(size / (cols + 1.2)) }, `shape-${i}`))) }) }));
}
function PileButton({ card, pileIdx, disabled, onPick, label }) {
    return (_jsx("button", { type: "button", disabled: disabled, "aria-label": label, onClick: () => !disabled && onPick(pileIdx), className: [
            'flex flex-col items-center gap-s1',
            'transition-all duration-feedback ease-soft-wind',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer active:scale-95',
        ].join(' '), children: _jsx(CardFace, { card: card, size: 56 }) }));
}
export function WcstStaircaseView({ adapter, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handlePick = useCallback((idx) => adapter.handleAnswer(idx), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const awaiting = adapterState.subPhase === 'awaiting_input';
    const testCard = adapterState.stim?.testCard ?? null;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsx("div", { className: "flex items-end justify-center gap-s2", children: REFERENCE_PILES.map((pile) => (_jsx(PileButton, { card: pile.card, pileIdx: pile.idx, disabled: !awaiting, onPick: handlePick, label: t('wcst.play.pile_aria', { idx: pile.idx + 1 }) }, `pile-${pile.idx}`))) }), _jsx("div", { className: "flex h-[16px] items-center justify-center text-[12px] text-cyan-text-secondary", children: t('wcst.play.hint_pick_pile') }), _jsxs("div", { className: "flex h-[140px] items-center justify-center", children: [adapterState.subPhase === 'fixation' && (_jsx("span", { className: "font-display text-[36px] text-cyan-text-secondary", children: "+" })), testCard && (adapterState.subPhase === 'awaiting_input' || adapterState.subPhase === 'feedback') && (_jsx(CardFace, { card: testCard, size: 64 }))] }), _jsx("div", { className: "flex h-[24px] items-center justify-center text-[14px]", children: adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                        'font-display text-[18px] font-medium',
                        adapterState.feedback === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                    ].join(' '), children: adapterState.feedback === 'correct' ? '✓' : '✗' })) })] }));
}
export function WcstDebriefView() {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : threshold.toFixed(1).replace(/\.0$/, '');
    const replay = () => {
        window.location.reload();
    };
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('wcst.debrief.title'), scoreLabel: t('wcst.debrief.score_label'), scoreValue: thresholdLabel, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
        ], recommendation: t('wcst.debrief.recommendation'), scientificCaveat: t('wcst.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map